import { ref, reactive, onActivated, onMounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Edit, Delete } from '@element-plus/icons-vue'
import { request } from '@/util/globalContext';

// 角色列表逻辑处理
export default function useRoleList(menuTreeRef) {
    // ========== 响应式数据定义 ==========
    // 菜单配置弹窗相关
    const configMenuDialogVisible = ref(false) // 菜单配置弹窗显示状态
    const menuTreeData = ref([]) // 菜单树数据
    const checkedMenuKeys = ref([]) // 树节点选中的菜单ID
    const treeProps = { // 树形结构配置项
        label: 'menuName',
        children: 'children'
    }

    // 搜索表单（默认值初始化）
    const searchForm = reactive({
        roleId: '',
        roleName: '',
        isDeleted: '',
        createTime: '',
        updateTimeRange: []
    })

    // 列表及弹窗核心响应式数据
    const listRef = ref(null) // 列表组件引用
    const dialogVisible = ref(false) // 新增/编辑弹窗显示状态
    const dialogType = ref('add') // 弹窗类型：add-新增 edit-编辑
    const formRef = ref(null) // 表单组件引用
    const selectedRow = ref(null) // 当前选中的行数据

    // 表单默认值
    const FORM_DEFAULT = {
        roleId: '',
        roleName: '',
        isDeleted: 0,
        roleDesc: ''
    }
    const formData = reactive({ ...FORM_DEFAULT }) // 表单绑定数据

    // 表单校验规则
    const formRules = reactive({
        roleId: [
            { required: true, message: '请输入角色ID', trigger: 'blur' },
            { max: 25, message: '角色ID长度不能超过25个字符', trigger: 'blur' }
        ],
        roleName: [
            { required: true, message: '请输入角色名称', trigger: 'blur' },
            { max: 25, message: '角色名称长度不能超过25个字符', trigger: 'blur' }
        ],
        isDeleted: [{ required: true, message: '请选择是否生效', trigger: 'change' }],
        roleDesc: [
            { max: 100, message: '角色描述长度不能超过100个字符', trigger: 'blur' }
        ]
    })

    // ========== 核心方法 - 列表加载 ==========
    // 加载角色列表（含分页、搜索参数）
    const handleLoadList = async (paginationParams) => {
        if (!listRef.value) return
        // 校验列表组件初始化状态
        if (!listRef.value.isInited) {
            await nextTick()
            if (!listRef.value.isInited) {
                ElMessage.warning('列表组件初始化中，请稍后重试')
                return
            }
        }

        listRef.value.setLoading(true)
        try {
            // 组装请求参数（分页+搜索+时间范围拆解）
            const params = {
                ...paginationParams,
                ...searchForm,
                updateTimeStart: searchForm.updateTimeRange[0] || '',
                updateTimeEnd: searchForm.updateTimeRange[1] || ''
            }
            const res = await request.get('/role/getRoleList', { params })
            if (res.code === 200) {
                // 更新列表数据和分页信息
                listRef.value.updateListData(res.data.records || [])
                listRef.value.updatePagination({
                    pageNum: paginationParams.pageNum,
                    pageSize: paginationParams.pageSize,
                    total: res.data.total || 0
                })
            } else {
                ElMessage.error('列表加载失败：' + (res.msg || '接口异常'))
                listRef.value.updateListData([])
                listRef.value.updatePagination({ ...paginationParams, total: 0 })
            }
        } catch (error) {
            ElMessage.error('列表加载失败：网络异常')
            console.error('加载角色列表失败：', error)
            listRef.value.updateListData([])
            listRef.value.updatePagination({ ...paginationParams, total: 0 })
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // ========== 列表交互 - 行选择 ==========
    // 列表勾选行变化
    const handleSelectionChange = (val) => {
        selectedRow.value = val?.length ? val[0] : null
    }
    // 列表当前行变化
    const handleCurrentRowChange = (val) => {
        selectedRow.value = val
    }

    // ========== 弹窗操作 - 新增/编辑 ==========
    // 新增角色（重置表单+打开弹窗）
    const handleAdd = () => {
        dialogType.value = 'add'
        formRef.value?.resetFields() // 清空表单校验状态
        Object.assign(formData, FORM_DEFAULT) // 重置表单数据
        dialogVisible.value = true
    }

    // 编辑角色（获取选中行详情+打开弹窗）
    const handleEdit = async () => {
        if (!selectedRow.value) {
            ElMessage.warning('请先选中一条角色数据！')
            return
        }
        if (!listRef.value?.isInited) {
            ElMessage.warning('列表组件初始化中，请稍后重试')
            return
        }

        try {
            listRef.value.setLoading(true)
            // 查询选中角色详情
            const res = await request.get('/role/getRoleList', {
                params: { roleId: selectedRow.value.roleId }
            })

            if (res.code !== 200) {
                ElMessage.error('获取角色详情失败：' + (res.msg || '接口异常'))
                return
            }
            // 赋值表单数据
            const roleData = res.data.records[0]
            formData.roleId = roleData.roleId
            formData.roleName = roleData.roleName
            formData.isDeleted = roleData.isDeleted
            formData.roleDesc = roleData.roleDesc || ''

            dialogType.value = 'edit'
            dialogVisible.value = true
        } catch (error) {
            ElMessage.error('获取角色详情失败' + error)
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // ========== 角色操作 - 失效 ==========
    // 失效角色（确认弹窗+接口调用）
    const handleDelete = async () => {
        if (!selectedRow.value) {
            ElMessage.warning('请先选中一条角色数据！')
            return
        }
        // 校验角色当前状态（已失效则提示）
        if (selectedRow.value.isDeleted === 1) {
            ElMessage.warning('该角色已处于失效状态！')
            return
        }

        try {
            // 确认弹窗
            await ElMessageBox.confirm(
                '确定要失效该角色吗？',
                '失效确认',
                { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
            )

            listRef.value.setLoading(true)
            // 调用失效接口
            const res = await request.post('/role/delRole', { roleId: selectedRow.value.roleId })
            if (res.code === 200) {
                ElMessage.success('失效成功')
                // 刷新列表
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                ElMessage.error('失效失败：' + (res.msg || '接口异常'))
            }
        } catch (error) {
            // 排除取消弹窗的异常
            if (error !== 'cancel') {
                ElMessage.error('失效失败：网络异常')
            }
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // ========== 表单操作 - 提交/重置 ==========
    // 提交新增/编辑表单（校验+接口调用）
    const handleSubmit = async () => {
        if (!formRef.value) return

        // 表单校验（返回布尔值）
        const isValid = await new Promise((resolve) => {
            formRef.value.validate((valid) => {
                resolve(valid);
            });
        });
        if (!isValid) return;

        listRef.value.setLoading(true)
        try {
            // 调用保存/更新接口
            const res = await request.post('/role/saveOrUpdateRole', { ...formData, dialogType: dialogType.value })
            if (res.code === 200) {
                ElMessage.success(res.msg)
                dialogVisible.value = false // 关闭弹窗
                // 刷新列表
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                const tip = dialogType.value === 'add' ? '新增失败' : '修改失败'
                ElMessage.error(tip + '：' + (res.msg || '接口异常'))
            }
        } catch (error) {
            ElMessage.error('接口异常:' + error)
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // 重置搜索表单
    const handleReset = () => {
        Object.keys(searchForm).forEach((key) => {
            searchForm[key] = Array.isArray(searchForm[key]) ? [] : ''
        })
    }

    // ========== 菜单配置 - 数据处理 ==========
    // 格式化菜单数据（排序+提取选中ID）
    const formatMenuData = (data) => {
        if (!Array.isArray(data)) return [];
        const checkedKeys = [];

        // 递归排序菜单+收集选中ID
        const recursionFormat = (list) => {
            // 按sortNum升序排序
            const sortedList = [...list].sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0));
            return sortedList.map(item => {
                const menuIdStr = String(item.menuId);
                // 仅收集叶子节点（无子菜单）且 checkType === 0 的菜单ID
                //Element Plus 自动根据子节点勾选 实现父节点的全选半选
                if (!item.children || item.children.length === 0) {
                    if (item.checkType === 0) {
                        checkedKeys.push(menuIdStr);
                    }
                }
                // 递归处理子菜单
                if (item.children && item.children.length) {
                    item.children = recursionFormat(item.children);
                }
                return item;
            });
        };

        const formattedData = recursionFormat(data);
        checkedMenuKeys.value = checkedKeys; // 赋值选中ID
        return formattedData;
    }

    // ========== 菜单配置 - 弹窗操作 ==========
    // 打开菜单配置弹窗（加载角色关联菜单）
    const handleConfigMenu = async (row) => {
        if (!row.roleId) {
            ElMessage.error('参数为空无法配置');
            return
        }
        try {
            listRef.value.setLoading(true)
            // 查询角色关联菜单
            const res = await request.get('/menu/getMenuByRole', { params: { roleId: row.roleId } })
            if (res.code === 200) {
                // 格式化菜单数据
                menuTreeData.value = formatMenuData(res.data || []);
                configMenuDialogVisible.value = true;
                // 等待DOM渲染后设置选中状态
                await nextTick();
                if (menuTreeRef.value) {
                    menuTreeRef.value.setCheckedKeys(checkedMenuKeys.value);
                }
            } else {
                ElMessage.error(res.msg || '查询菜单失败')
            }
        } catch (error) {
            ElMessage.error('接口异常:' + error)
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // 收集菜单配置数据（获取全选+半选的所有menuId）
    const collectMenuData = () => {
        // 1. 校验Tree组件是否初始化，避免报错
        if (!menuTreeRef.value) {
            ElMessage.warning('菜单树组件未初始化，无法收集配置数据');
            return [];
        }

        // 2. 核心：通过Tree原生API获取「全选节点ID」+「半选节点ID」
        const checkedKeys = menuTreeRef.value.getCheckedKeys(); // 全选节点ID（父/子节点都包含）
        const halfCheckedKeys = menuTreeRef.value.getHalfCheckedKeys(); // 半选节点ID（仅父节点）

        // 3. 合并并去重（避免极端场景下重复ID，实际API返回无重复，兜底处理）
        const allSelectedMenuIds = Array.from(new Set([...checkedKeys, ...halfCheckedKeys]))
            // 过滤无效ID（如空/undefined）
            .filter(menuId => menuId && String(menuId).trim());

        // 4. 格式转换（接口需[{menuId: xxx}]结构，无需checkType字段）
        const result = allSelectedMenuIds.map(menuId => ({ menuId }));

        // 5. 空数据提示（保持原逻辑）
        // if (!result.length) {
        //     ElMessage.warning('请先配置菜单');
        // }

        return result;
    };

    // 提交菜单配置（保存角色-菜单关联关系）
    const handleConfigMenuSubmit = async () => {
        const submitData = collectMenuData();
        if (!submitData.length) {
            await ElMessageBox.confirm(
                '确定去除角色所有菜单？',
                '警告',
                {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning',
                    dangerouslyUseHTMLString: true
                }
            );
        }

        try {
            // 调用保存权限菜单配置
            const res = await request.post('/role/saveOrUpdateRoleMenu', {
                roleId: selectedRow.value.roleId,
                menuList: submitData
            });
            if (res.code === 200) {
                ElMessage.success('菜单配置成功');
                configMenuDialogVisible.value = false;
                // 刷新列表
                const pagination = listRef.value.getPagination();
                handleLoadList(pagination);
            } else {
                ElMessage.error('菜单配置失败：' + (res.msg || '接口异常'));
            }
        } catch (error) {
            ElMessage.error('接口异常:' + error);
        }
    }

    // ========== 生命周期 ==========
    // 挂载时加载列表
    onMounted(async () => {
        await nextTick()
        setTimeout(() => {
            if (listRef.value?.isInited) {
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            }
        }, 50)
    })
    // 激活时刷新列表（keep-alive场景）
    onActivated(() => {
        if (listRef.value?.isInited) {
            const pagination = listRef.value.getPagination()
            handleLoadList(pagination)
        }
    })

    // ========== 暴露变量/方法 ==========
    return {
        // 响应式数据
        searchForm,
        listRef,
        dialogVisible,
        dialogType,
        formRef,
        selectedRow,
        formData,
        formRules,
        // 图标
        Plus, Edit, Delete,
        // 列表交互
        handleSelectionChange,
        handleCurrentRowChange,
        // 角色操作
        handleAdd,
        handleEdit,
        handleDelete,
        handleSubmit,
        handleLoadList,
        handleReset,
        // 菜单配置
        handleConfigMenu,
        configMenuDialogVisible,
        menuTreeData,
        treeProps,
        handleConfigMenuSubmit,
        checkedMenuKeys
    }
}